import React, { useContext, useState } from 'react';
import { DateTime } from 'luxon';

import { downloadPdfFromBlobResponse } from 'helpers/pdf';

import { media } from 'context';
import { Grid, PillButton } from 'ds';
import Avatar from 'ds/Avatar';
import Layout from 'ds/Layout';
import Lightbox from 'ds/Lightbox';
import MessageImageThumbnail from 'ds/MessageThread/MessageImageThumbnail';
import MessagePdfThumbnail from 'ds/MessageThread/MessagePdfThumbnail';
import RichTextDisplay from 'ds/RichTextDisplay';
import Text from 'ds/Text';
import { Message } from 'shared';
import axios from 'shared/lib/axios';
import { selectUIState } from 'store/UI/selectors';
import { useAppSelector } from 'store/hooks';

import MessageEmojiSelector from './MessageEmojiSelector';
import MessageEmojis from './MessageEmojis';
import { namePartsToFirstLastInitial } from './utils';

interface Props {
  message: Message;
  supportTicketId: number;
  isLastMessageInGroup: boolean;
  onEmojiSelect: ({ emoji, messageId }: { emoji: string; messageId: number }) => void;
  onCreateLearning?: (message: Message) => void;
  supportTicketTitle?: string;
  aiAssistantConversationId?: number;
  showAbsoluteTime: boolean;
  onToggleTimeFormat: () => void;
  readOnly?: boolean;
}

const SupportTicketCustomerMessage: React.FC<Props> = ({
  message,
  supportTicketId,
  onEmojiSelect,
  onCreateLearning,
  showAbsoluteTime,
  onToggleTimeFormat,
  readOnly = false
}) => {
  const { id, author, author_email, body, images, files, created_at, emojis } = message;
  const { avatar_url, first, last, customer, is_admin } = author ?? {};
  const [lightboxStartingIndex, setLightboxStartingIndex] = useState<number>(-1);
  const { xs, isMobile, isDesktop } = useContext(media);
  const reduxUxType = useAppSelector(selectUIState).uxType;
  const isCodiAdminUx = reduxUxType === 'admin';
  const isUsersMessage = !is_admin;
  let customerName = '';

  if (isUsersMessage && customer) {
    customerName = `${customer.name}`;
  } else if (is_admin) {
    customerName = 'Codi';
  }

  const messageDate = DateTime.fromISO(created_at);
  const timeDisplay = showAbsoluteTime ? messageDate.toFormat('LLL d, h:mm a') : messageDate.toRelative();

  function downloadFile({ fileId }: { fileId: number }) {
    return axios({
      url: `/v1/support_tickets/${supportTicketId}/messages/${id}/download_file?file_id=${fileId}`,
      method: 'GET',
      responseType: 'blob'
    }).then(response => {
      downloadPdfFromBlobResponse(response);
    });
  }

  const showEmojiUI = !readOnly;
  const showFiles = !readOnly && !!files.length;

  return (
    <Layout flexGrow={1} __className="SupportTicketMessage">
      {lightboxStartingIndex >= 0 && (
        <Lightbox
          images={images}
          startingIndex={lightboxStartingIndex}
          onClose={() => setLightboxStartingIndex(-1)}
          padResize
        />
      )}
      <Layout flex flexGrow={1}>
        <Layout minWidth={32} minHeight={32}>
          <Avatar size={32} avatarUrl={avatar_url} borderWidth={0} defaultIcon="avatar" />
        </Layout>
        <Layout marginLeft={16} />
        <Layout direction="column" flexGrow={1}>
          <Layout justify="space-between" align="center">
            <Layout paddingY={6} gap={8} flex>
              {!!author_email ? (
                <Text size="body-xs" ellipsis>
                  {author_email}
                </Text>
              ) : (
                <Text size="body-xs" ellipsis>
                  {namePartsToFirstLastInitial({
                    firstname: first!,
                    lastname: last!
                  })}
                  {` • ${customerName}`}
                </Text>
              )}
              <Layout flexShrink={0} align="center">
                <button
                  onClick={onToggleTimeFormat}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: 'inherit'
                  }}
                >
                  <Text size="body-xs" color="gray-400">
                    {timeDisplay}
                  </Text>
                </button>
              </Layout>
            </Layout>
            <Layout gap={8} align="center">
              {showEmojiUI && is_admin && onCreateLearning && (
                <Layout __className="CreateLearningButton" display="inline-flex">
                  <PillButton
                    size="sm"
                    type="primary"
                    text="Create learning"
                    onClick={() => onCreateLearning(message)}
                  />
                </Layout>
              )}
              {showEmojiUI && isDesktop && !emojis.length && (
                <MessageEmojiSelector onEmojiSelect={onEmojiSelect} messageId={id} hasEmojis={!!emojis.length} />
              )}
            </Layout>
          </Layout>
          <Layout marginTop={4} />
          <Layout direction="column">
            <Layout align="flex-start">
              <Layout maxWidth={isMobile ? `calc(100vw - ${isCodiAdminUx ? 144 : 88}px)` : 556}>
                <Text size="body-sm">
                  <RichTextDisplay value={body} />
                </Text>
              </Layout>
            </Layout>
            {!!images.length && (
              <Layout marginTop={24} flex wrap position="relative" left={-6} width="calc(100% + 12px)">
                {images.map(({ key }, index) => {
                  return (
                    <MessageImageThumbnail
                      onClick={() => setLightboxStartingIndex(index)}
                      style={{
                        margin: xs ? '10px 6px' : '8px 6px',
                        width: 98,
                        height: 98
                      }}
                      key={key}
                      srcKey={key}
                    />
                  );
                })}
              </Layout>
            )}
            {showFiles && (
              <Layout
                marginTop={!images.length ? 16 : 0}
                flex
                wrap
                position="relative"
                left={-6}
                width="calc(100% + 12px)"
              >
                <Grid gap={8} itemsPerRow={1}>
                  {files.map(({ id, key, filename, filesize }) => {
                    return (
                      <MessagePdfThumbnail
                        onClick={() => {
                          downloadFile({ fileId: id });
                        }}
                        key={key}
                        filename={filename}
                        filesize={filesize}
                      />
                    );
                  })}
                </Grid>
              </Layout>
            )}
            {showEmojiUI &&
              (isDesktop ? (
                !!emojis.length && (
                  <Layout marginTop={8} flex align="center" gap={8}>
                    {emojis.length > 0 && (
                      <MessageEmojis emojis={emojis} messageId={id} onEmojiSelect={onEmojiSelect} />
                    )}
                    <MessageEmojiSelector onEmojiSelect={onEmojiSelect} messageId={id} hasEmojis={!!emojis.length} />
                  </Layout>
                )
              ) : (
                <Layout marginTop={8} align="center" gap={8} wrap>
                  {emojis.length > 0 && <MessageEmojis emojis={emojis} messageId={id} onEmojiSelect={onEmojiSelect} />}
                  {isMobile && (
                    <MessageEmojiSelector onEmojiSelect={onEmojiSelect} messageId={id} hasEmojis={!!emojis.length} />
                  )}
                </Layout>
              ))}
          </Layout>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default SupportTicketCustomerMessage;
